import { RadioGroup as MuiRadioGroup, RadioGroupProps as MuiRadioGroupProps } from '@mui/material';
import {
    FieldPath, FieldValues, UseControllerProps, useController
} from 'react-hook-form';


type RadioGroupProps<
    TFieldValues extends FieldValues,
    TName extends FieldPath<TFieldValues>
> = UseControllerProps<TFieldValues, TName> & MuiRadioGroupProps;

/**
 * Renders a react-hook-form controlled mui RadioGroup component.
 *
 * @example
 * ```ts
 * import { FormProvider, useForm } from 'react-hook-form';
 * import { Radio } from '@mui/material';
 *
 * const formMethods = useForm();
 *
 * <FormProvider {...formMethods}>
 *     <RadioGroup name="name">
 *         <Radio value="one" />
 *         <Radio value="two" />
 *     </RadioGroup
 * </FormProvider>
 * ```
 *
 * @param props - The props for the Controller and the underlying RadioGroup
 * @constructor
 */
export function RadioGroup<
    TFieldValues extends FieldValues = FieldValues,
    TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
>(props: RadioGroupProps<TFieldValues, TName>) {
    const {
        rules, name, defaultValue, ...otherProps
    } = props;

    const { field: fieldProps } = useController({
        name,
        rules,
        defaultValue
    });

    return (
        <MuiRadioGroup
            {...otherProps}
            {...fieldProps}
        />
    );
}
